'use client';

import type { FC } from 'react';
import { memo, useRef } from 'react';
import { cn } from '../../utils/cn';
import { Button } from '../ui';
import { Credits } from '../Credits';
import { UserAvatar } from '../UserAvatar/UserAvatar';
import { FaCrown, GiLockedChest } from '../icons';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { BandFinalBadge, FormatBadge, LiveDot, ModeBadge, formatMmSs, useCaseBattleT, useCountdown } from '../caseBattle/atoms';
import { isBandFunded, seatsBySlot } from '../caseBattle/battleModel';
import { cardShowState } from '../caseBattle/cardShowState';
import type { BattleData } from './BattleCard';
import { CaseStrip } from './CaseStrip';
import { winnerSlots } from './CardFooter';
import translations from './BattleCard.translations';
import '../../screens/BandScreen/BandNeon.css';

const ROW_GRID = 'grid grid-cols-[180px_minmax(0,1fr)_140px_200px_168px] items-center gap-5 px-4';

export const BattleRowHeader: FC = () => {
  const { t } = useLocalTranslation(translations);
  const { t: tBattle } = useCaseBattleT();
  const cell = 'text-[11px] font-semibold uppercase tracking-[0.06em] text-label-tertiary';
  return (
    <div className={cn(ROW_GRID, 'pb-1')}>
      <span className={cell}>{t('colBattle')}</span>
      <span className={cell}>{t('colCases')}</span>
      <span className={cell}>{tBattle('entry')}</span>
      <span className={cell}>{t('colPlayers')}</span>
      <span />
    </div>
  );
};

interface BattleRowProps {
  battle: BattleData;
  onOpen?: (battleId: string) => void;
  onJoin?: (battleId: string) => void;
  now: number;
  variant?: 'lobby' | 'history';
  isBandFinal?: boolean;
}

export const BattleRow: FC<BattleRowProps> = memo(function BattleRow({ battle, onOpen, onJoin, now, variant = 'lobby', isBandFinal = false }: BattleRowProps) {
  const { t } = useLocalTranslation(translations);
  const { t: tBattle } = useCaseBattleT();
  const bandFinal = isBandFinal || isBandFunded(battle);
  const show = cardShowState(battle, now, variant === 'lobby');
  const { running, flight, flightColor, currentRound } = show;
  const status: string = battle.status;
  const waiting = status === 'WAITING';
  const deadlineLeft = useCountdown(waiting ? battle.deadlineAt : null);
  const currentCaseRef = useRef<HTMLSpanElement>(null);
  const seats = seatsBySlot(battle.maxPlayers, battle.participants);
  const winners = winnerSlots(battle);

  const statusLabel = running
    ? tBattle('statusRunning')
    : status === 'COMPLETED'
      ? tBattle('statusCompleted')
      : status === 'CANCELLED'
        ? tBattle('statusCancelled')
        : tBattle('statusWaiting');
  const ctaKey = running ? 'watch' : status === 'COMPLETED' ? 'results' : status === 'CANCELLED' ? 'cancelled' : null;
  const joinable = onJoin !== undefined && ctaKey === null;

  const row = (
    <div
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen ? () => onOpen(battle.id) : undefined}
      onKeyDown={onOpen ? (e) => { if (e.key === 'Enter') onOpen(battle.id); } : undefined}
      className={cn(
        ROW_GRID,
        'rounded-2xl bg-bg-secondary py-3 transition-shadow duration-500',
        bandFinal && 'relative isolate overflow-hidden',
        onOpen && 'cursor-pointer hover:bg-white/[0.06]',
        battle.mine && 'ring-1 ring-accent-blue/40',
        running && !flight && 'ring-1 ring-accent-green/35',
        running && flight && 'ring-2',
      )}
      style={flight ? { '--tw-ring-color': flightColor, boxShadow: `0 0 28px -8px ${flightColor}` } : undefined}
    >
      {bandFinal && <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 opacity-60 bnc-battle-tint" />}
      <div className="flex min-w-0 flex-col gap-2">
        <span className="inline-flex items-center gap-1.5">
          <FormatBadge isTeam={battle.isTeam} maxPlayers={battle.maxPlayers} isPrivate={battle.visibility === 'private'} />
          {bandFinal ? <BandFinalBadge /> : <ModeBadge mode={battle.mode} />}
        </span>
        {running ? (
          <span className="inline-flex items-center gap-1.5 text-[13px] font-bold tabular-nums text-accent-green">
            <LiveDot />
            {statusLabel}
            {currentRound !== null && ` · ${t('round')} ${currentRound + 1}/${battle.rounds.length}`}
          </span>
        ) : (
          <span className="text-[13px] font-semibold tabular-nums text-label-secondary">
            {statusLabel}
            {waiting && battle.deadlineAt && <span className="text-label-tertiary"> · {formatMmSs(deadlineLeft)}</span>}
          </span>
        )}
      </div>

      <CaseStrip rounds={battle.rounds} currentRound={currentRound} dropColor={flight ? flightColor : null} currentCaseRef={currentCaseRef} size={64} />

      <div className="flex flex-col gap-1">
        <span className="text-[20px] font-bold leading-none tracking-[-0.4px] tabular-nums text-label-primary">
          <Credits amount={battle.totalCost} iconSize={15} />
        </span>
        <span className="inline-flex items-center gap-1 text-[13px] font-semibold tabular-nums text-label-secondary">
          <GiLockedChest size={14} />
          {battle.rounds.length}
        </span>
      </div>

      <div className="flex items-center gap-2">
        {seats.map((seat, slot) => (
          <span key={slot} className="contents">
            {battle.isTeam && slot === 2 && <span dir="ltr" className="text-[13px] leading-none text-label-tertiary">×</span>}
            {seat ? (
              <span className="relative shrink-0">
                {winners.has(slot) && <FaCrown size={12} className="absolute left-1/2 -top-2 -translate-x-1/2 text-accent-yellow" />}
                <UserAvatar userId={seat.userId} firstName={seat.firstName ?? null} level={seat.level ?? 1} size="s" />
              </span>
            ) : (
              <span className="h-8 w-8 shrink-0 rounded-full border border-dashed border-label-quaternary" />
            )}
          </span>
        ))}
      </div>

      <div className="flex justify-end">
        {joinable ? (
          <Button
            size="m"
            onClick={(e) => {
              e.stopPropagation();
              onJoin(battle.id);
            }}
            className="h-9 px-4 py-0 text-[14px] font-semibold"
          >
            {t('join')} <Credits amount={battle.totalCost} iconSize={12} compact />
          </Button>
        ) : ctaKey ? (
          <Button
            size="m"
            variant="tertiary"
            onClick={(e) => {
              e.stopPropagation();
              onOpen?.(battle.id);
            }}
            className={cn('h-9 bg-fill-tertiary px-4 py-0 text-[14px] font-semibold', status === 'CANCELLED' && 'text-label-tertiary')}
          >
            {t(ctaKey)}
          </Button>
        ) : null}
      </div>
    </div>
  );

  return bandFinal ? <div className="rounded-2xl p-[1.5px] bnc-battle-ring">{row}</div> : row;
});
